// Shared domain enums and types — single source of truth across api / web / mobile.

export const ROLES = ["owner", "admin", "member"] as const;
export type Role = (typeof ROLES)[number];

export const INTERVIEW_TYPES = [
  "technical",
  "non_technical",
  "language",
  "behavioral",
  "sales",
  "customer_support",
  "leadership",
  "general_screening",
] as const;
export type InterviewType = (typeof INTERVIEW_TYPES)[number];

// "entry"…"executive" are the surfaced ladder; "intern"/"staff" are retained so
// older interviews created before the ladder change still validate on edit.
export const SENIORITY = [
  "entry",
  "junior",
  "mid",
  "senior",
  "lead",
  "manager",
  "executive",
  "intern",
  "staff",
] as const;
export type Seniority = (typeof SENIORITY)[number];

export const INVITATION_STATUS = [
  "invited",
  "scheduling",
  "scheduled",
  "in_progress",
  "completed",
  "evaluated",
  "expired",
  "cancelled",
] as const;
export type InvitationStatus = (typeof INVITATION_STATUS)[number];

export const BILLING_STATUS = ["trialing", "active", "past_due", "delinquent"] as const;
export type BillingStatus = (typeof BILLING_STATUS)[number];

export const RECOMMENDATION = ["strong_yes", "yes", "maybe", "no", "strong_no"] as const;
export type Recommendation = (typeof RECOMMENDATION)[number];

// Hiring decision lifecycle tracked per invitation (employer-side).
export const DECISION_STATUS = [
  "new",
  "needs_review",
  "shortlisted",
  "rejected",
  "reinterview",
  "next_round",
  "hired",
  "archived",
] as const;
export type DecisionStatus = (typeof DECISION_STATUS)[number];

// Graded risk level used across integrity signals and evidence cards.
export const RISK_LEVEL = ["low", "medium", "high", "critical"] as const;
export type RiskLevel = (typeof RISK_LEVEL)[number];

// A point on the interview evidence timeline. Either captured by proctoring
// (source: "proctor"), derived from interview lifecycle (source: "system"), or
// added by a reviewer (source: "manual").
export interface EvidenceEvent {
  id?: string;
  at: string; // ISO timestamp
  type: string; // event code, e.g. "interview_started", "looked_away"
  riskLevel: RiskLevel;
  note: string;
  imageUrl?: string | null;
  source: "system" | "proctor" | "manual";
}

export interface RubricDimension {
  key: string;
  label: string;
  weight: number; // 0..1, dimensions should sum ~1
  description: string;
}

export interface QuestionPlanItem {
  id: string;
  prompt: string;
  followups?: string[];
  skill?: string;
}

export interface DimensionScore {
  key: string;
  label: string;
  score: number; // 0..100
  rationale: string;
}

export interface IntegrityFlag {
  code: string; // e.g. "gaze_off_screen", "multiple_faces", "paste_detected"
  severity: "info" | "warn" | "critical";
  detail: string;
  source: "tavus" | "client" | "transcript";
  at?: string;
}

export interface EvaluationResult {
  overallScore: number; // 0..100
  recommendation: Recommendation;
  summary: string;
  strengths: string[];
  concerns: string[];
  dimensionScores: DimensionScore[];
  integrityFlags: IntegrityFlag[];
}
